import {
  Button,
  Box,
  Card as MuiCard,
  CardContent,
  Divider as MuiDivider,
  FormControl as MuiFormControl,
  TextField as MuiTextField,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
//Material UI Components and Functions
import { makeStyles } from "@mui/styles";
import { ExitToApp } from "@mui/icons-material";
import MuiAlert from "@mui/material/Alert";
import { spacing } from "@mui/system";
// Redux Components
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import styled from "styled-components";
import { txtBoxDateFormatter } from "../../../setup/helpers";
import {
  createNewAccountBill,
  getUserBills,
  finalizePrint,
  updateNewAccountBillInProgress,
  finalizeBill
} from "../../bills/api/actions";
import { setSnackbar } from "../../snackbar/api/snackbar";
// Custom UI componets and functions:
import AdditionalBillLineItemsManager from "./AdditionalBillLineIItemsChart";

const CenteredContent = styled.div`
  text-align: center;
`;

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

// Safe Transition component that doesn't access scrollTop
// This prevents errors when the container becomes null during dialog unmount
const SafeTransition = React.forwardRef(function SafeTransition(props, ref) {
  const { in: inProp, children } = props;
  
  // Simple fade-in without accessing scrollTop
  const [opacity, setOpacity] = useState(0);
  
  useEffect(() => {
    if (inProp) {
      // Use requestAnimationFrame to avoid accessing scrollTop
      requestAnimationFrame(() => {
        setOpacity(1);
      });
    } else {
      setOpacity(0);
    }
  }, [inProp]);
  
  return (
    <div
      ref={ref}
      style={{
        opacity: opacity,
        transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      }}
    >
      {children}
    </div>
  );
});

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const FormControl = styled(MuiFormControl)(spacing);
const TextField = styled(MuiTextField)(spacing);

// Custom Styles for property selection drop down

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  selectedPropertyLocationLabel: {
    fontSize: 50,
    backgroundColor: "#fffffff",
    borderColor: "#fffffff",
  },
  loadingIcon: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
  },
  billForm: {
    display: "none",
  },
}));

const BillForm = (props) => {
  // Handles vendor drop down
  const [vendor, setVendor] = React.useState(props.bills.newBill.vendorID);
  const [newBillFormObject, setNewBillFormObject] = React.useState(
    props.bills.newBill
  );
  const [isFullBillDisplay, setIsFullBillDisplay] = useState("inline-block");
  const [successState, setSuccessState] = useState(false);
  const [errorState, setErrorState] = useState(false);

  const dispatch = useDispatch();
  const handleSuccess = () => {
    setSuccessState(true);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSuccessState(false);
    setErrorState(false);
  };

  // passed props
  const accountNum = props.accountNum;

  const handleSelectedVendorChange = (event) => {
    onFieldChange(event);
    setVendor(event.target.value);
  };

  const submitBillForm = async () => {
    try {

      /*

        Validate Bill before it's created.
      */
      if(props.bills.newBill.serviceEndDate == "" || props.bills.newBill.serviceEndDate == "") {
        dispatch(setSnackbar(true, "error", "Missing service dates on new bill"));
        return;
      }

      if(props.bills.newBill.newBillLineItems.items.length <= 0) {
        dispatch(setSnackbar(true, "error", "You must add a bill line item before a bill can be created."));
        return;
      }
      let validateItems = false;
      for(let bli of props.bills.newBill.newBillLineItems.items){
        if(bli.totalCharge <= 0) {
          dispatch(setSnackbar(true, "error", "Bill item must have a postive charge."));
          validateItems = false;
          break;
        }

        if(bli.itemDesc == "") {
          dispatch(setSnackbar(true, "error", "Bill cost type must be selected"));
          validateItems = false;
          break;
        }
        validateItems = true;
      }

      if(!validateItems)
        return


      dispatch(setSnackbar(true, "warning", "Submitting Account Bill"));
      // Replaces existing store object with new object with modified sub data
      const isFullBill = isFullBillDisplay == "inline-block" ? true : false;
      props.updateNewAccountBillInProgress(props.bills.newBill);
      for (
        var x = 0;
        x < props.bills.newBill.newBillLineItems.items.length;
        x++
      ) {
        if (props.bills.newBill.newBillLineItems.items[x].totalCharge) {
          delete props.bills.newBill.newBillLineItems.items[x].totalCharge;
        }
      }

      //Generated New Bill
      let newBill = await props.createNewAccountBill(
        props.bills.newBill,
        isFullBill,
        accountNum
      );
      dispatch(setSnackbar(true, "warning", "Getting data required to create printed bill."));
      let newBillBody = await props.finalizeBill(newBill)
      dispatch(setSnackbar(true, "warning", "Generating printed bill."));
      await props.finalizePrint(newBillBody)

      console.log("calling success state?!");
      dispatch(setSnackbar(true, "success", "Account Billed Successfully"));
      
      // Delay onUpdate to allow snackbar to display
      // The parent component (AccountBillsTable) will close the snackbar before closing the dialog
      if (props.onUpdate) {
        setTimeout(() => {
          props.onUpdate();
        }, 2000);
      }
    } catch (err) {
      console.error("Error in submitBillForm:", err);
      // Error message should already be set by the action, but set a fallback
      const errorMessage = err.response?.data?.message || err.message || "Error Billing Account";
      if (!errorState) {
        dispatch(setSnackbar(true, "error", errorMessage));
        setErrorState(true);
      }
    }
  };

  const cancelBillForm = () => {
    if (props.onCancel) {
      props.onCancel();
    }
  };

  const classes = useStyles();
  // Updates the local state object of "bill in progress" upon each modification
  const onFieldChange = (event) => {
    // Gets the current vendor info data and updates it with the event's name and value:
    if (event.target.type == "number") {
      props.bills.newBill[event.target.name] = Number(event.target.value);
    } else {
      props.bills.newBill[event.target.name] = event.target.value;
    }
  };

  const onCheckIsFullBill = (e) => {
    setIsFullBillDisplay(e.target.checked ? "inline-block" : "none");
  };

  let errorMessage =
    props.bills.error && props.bills.error.message
      ? props.bills.error.message
      : null;

  // Ensure container always returns a valid element
  const getContainer = () => {
    return document.body || document.documentElement;
  };

  return (
    <div>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={successState}
        autoHideDuration={6000}
        onClose={handleClose}
        container={getContainer}
        TransitionComponent={SafeTransition}
        disablePortal={false}
      >
        <Alert onClose={handleClose} severity="success">
          Successfully submitted billable items.
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorState}
        autoHideDuration={6000}
        onClose={handleClose}
        container={getContainer}
        TransitionComponent={SafeTransition}
        disablePortal={false}
      >
        <Alert onClose={handleClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
      <Card mb={6} elevation={3}>
        <CardContent>
          <div style={{ display: isFullBillDisplay }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%' }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 100%', lg: '1 1 calc(50% - 4px)' } }}>
                <TextField
                  label="Next Read Date"
                  type="date"
                  onChange={onFieldChange}
                  name="nextReadDate"
                  defaultValue={txtBoxDateFormatter(
                    props.bills.newBill.billDate
                  )}
                  fullWidth
                  my={2}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 100%', lg: '1 1 calc(50% - 4px)' } }}>
                <TextField
                  id="dueDate"
                  label="Due Date"
                  type="date"
                  fullWidth
                  my={2}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  defaultValue={txtBoxDateFormatter(
                    props.bills.newBill.dueDate
                  )}
                  onChange={onFieldChange}
                  name="dueDate"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 100%', lg: '1 1 calc(50% - 4px)' } }}>
                <TextField
                  id="serviceStartDate"
                  label="Service Start Date"
                  type="date"
                  fullWidth
                  my={2}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  defaultValue={txtBoxDateFormatter(
                    props.bills.newBill.serviceStartDate
                  )}
                  onChange={onFieldChange}
                  name="serviceStartDate"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 100%', lg: '1 1 calc(50% - 4px)' } }}>
                <TextField
                  id="serviceEndDate"
                  label="Service End Date"
                  type="date"
                  fullWidth
                  my={2}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  defaultValue={txtBoxDateFormatter(
                    props.bills.newBill.serviceEndDate
                  )}
                  onChange={onFieldChange}
                  name="serviceEndDate"
                />
              </Box>
              <Box sx={{ flex: '1 1 100%' }}>
                <TextField
                  label="Bill Message"
                  id="customBillMessage"
                  multiline={true}
                  rowsMax={10}
                  variant="outlined"
                  my={2}
                  fullWidth
                  defaultValue={""}
                  onChange={onFieldChange}
                  name="billMessage"
                />
              </Box>
            </Box>
          </div>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, width: '100%' }}>
            <Box sx={{ flex: '1 1 100%' }}>
              <AdditionalBillLineItemsManager props={props} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, width: '100%' }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(50% - 12px)' } }}>
              <Button
                mr={1}
                variant="contained"
                color="secondary"
                fullWidth
                onClick={submitBillForm}
                my={2}
              >
                Submit new account bill
              </Button>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(50% - 12px)' } }}>
              <Button
                mr={1}
                variant="contained"
                color="primary"
                fullWidth
                onClick={cancelBillForm}
                my={2}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

// Component Properties
BillForm.propTypes = {
  // Store objects:
  user: PropTypes.object.isRequired,
  apl: PropTypes.object.isRequired,
  bills: PropTypes.object.isRequired,
  account: PropTypes.object.isRequired,

  createNewAccountBill: PropTypes.func.isRequired,
  updateNewAccountBillInProgress: PropTypes.func.isRequired,
  getUserBills: PropTypes.func.isRequired,
  finalizeBill: PropTypes.func.isRequired,
  finalizePrint: PropTypes.func.isRequired
};

// Component State
function BillFormState(state) {
  return {
    user: state.user,
    apl: state.apl,
    bills: state.bills,
    account: state.account,
  };
}
export default connect(BillFormState, {
  createNewAccountBill,
  updateNewAccountBillInProgress,
  finalizeBill,
  finalizePrint,
  getUserBills,
})(BillForm);
